import React, { useState, useMemo, useEffect, useContext } from 'react';
import api from '../../services/api';
import { getHaBounds, computePerHaFee, perimeterFeeForArea, layoutFeeForPlots, topoFeeForArea, asbuiltFeeForPlots } from './feeCalc';

// ─── DATA ────────────────────────────────────────────────────────────────────

const PERIMETER_DATA = [
  { id: 1, size: '0 – 700 m²', A: 957730.00, B: 766184.00, C: 622524.50, D: 478865.00, E: 335205.50 },
  { id: 2, size: '701 – 1,050 m²', A: 1005616.50, B: 804493.20, C: 653650.73, D: 502808.25, E: 351965.78 },
  { id: 3, size: '1,051 – 1,400 m²', A: 1053383.28, B: 842706.63, C: 684699.13, D: 526691.64, E: 368684.15 },
  { id: 4, size: '1,401 – 2,100 m²', A: 1100785.53, B: 880628.43, C: 715510.60, D: 550392.77, E: 385274.94 },
  { id: 5, size: '2,101 – 2,800 m²', A: 1147568.92, B: 918055.13, C: 745919.80, D: 573784.46, E: 401649.12 },
  { id: 6, size: '2,801 – 3,500 m²', A: 1193471.67, B: 954777.34, C: 775756.59, D: 596735.84, E: 417715.09 },
  { id: 7, size: '3,501 – 4,200 m²', A: 1238226.86, B: 990581.49, C: 804847.46, D: 619113.43, E: 433379.40 },
  { id: 8, size: '4,201 – 5,000 m²', A: 1281564.80, B: 1025251.84, C: 833017.12, D: 640782.40, E: 448547.68 },
  { id: 9, size: '5,001 – 9,999 m²', A: 2167486.56, B: 1733989.25, C: 1408866.26, D: 1083743.28, E: 758620.30 },
  { id: 10, size: '1 Ha', per_ha: true, ha_to: 1, A: 2281564.80, B: 1825251.84, C: 1483017.12, D: 1140782.40, E: 798547.68 },
  { id: 11, size: '1.1 – 5 Ha', per_ha: true, ha_to: 5, A: 1825251.84, B: 1460201.47, C: 1186413.70, D: 912625.92, E: 638838.14 },
  { id: 12, size: '5.1 – 10 Ha', per_ha: true, ha_to: 10, A: 1460201.47, B: 1168161.18, C: 949130.96, D: 730100.74, E: 511070.52 },
  { id: 13, size: '10.1 – 15 Ha', per_ha: true, ha_to: 15, A: 1168161.18, B: 934528.94, C: 759304.77, D: 584080.59, E: 408856.41 },
  { id: 14, size: '15.1 – 20 Ha', per_ha: true, ha_to: 20, A: 934528.94, B: 747623.15, C: 607443.81, D: 467264.47, E: 327085.13 },
  { id: 15, size: '20.1 – 25 Ha', per_ha: true, ha_to: 25, A: 747623.15, B: 598098.52, C: 485955.05, D: 373811.58, E: 261668.10 },
  { id: 16, size: '25.1 – 30 Ha', per_ha: true, ha_to: 30, A: 598098.52, B: 478478.82, C: 388764.04, D: 299049.26, E: 209334.48 },
  { id: 17, size: '30.1 – 35 Ha', per_ha: true, ha_to: 35, A: 478478.82, B: 382783.05, C: 311011.23, D: 239239.41, E: 167467.59 },
  { id: 18, size: '35.1 – 40 Ha', per_ha: true, ha_to: 40, A: 382783.05, B: 306226.44, C: 248808.99, D: 191391.53, E: 133974.07 },
  { id: 19, size: '40.1 – 45 Ha', per_ha: true, ha_to: 45, A: 306226.44, B: 244981.15, C: 199047.19, D: 153113.22, E: 107179.26 },
  { id: 20, size: '45.1 – 50 Ha', per_ha: true, ha_to: 50, A: 244981.15, B: 195984.92, C: 159237.75, D: 122490.58, E: 85743.40 },
];

const LAYOUT_DATA = [
  { id: 1, plots: '1 – 5', A: 699942.00, B: 559953.60, C: 454962.30, D: 349971.00, E: 244979.70 },
  { id: 2, plots: '6 – 10', A: 664944.90, B: 531955.92, C: 432214.19, D: 332472.45, E: 232730.72 },
  { id: 3, plots: '11 – 15', A: 631697.66, B: 505358.12, C: 410603.48, D: 315848.83, E: 221094.18 },
  { id: 4, plots: '16 – 20', A: 600112.77, B: 480090.22, C: 390073.30, D: 300056.39, E: 210039.47 },
  { id: 5, plots: '21 – 25', A: 570107.13, B: 456085.71, C: 370569.64, D: 285053.57, E: 199537.50 },
  { id: 6, plots: '26 – 30', A: 541601.78, B: 433281.42, C: 352041.16, D: 270800.89, E: 189560.62 },
  { id: 7, plots: '31 – 35', A: 514521.69, B: 411617.35, C: 334439.10, D: 257260.84, E: 180082.59 },
  { id: 8, plots: '36 – 40', A: 488795.60, B: 391036.48, C: 317717.14, D: 244397.80, E: 171078.46 },
  { id: 9, plots: '41 – 45', A: 464355.82, B: 371484.66, C: 301831.29, D: 232177.91, E: 162524.54 },
  { id: 10, plots: '46 – 50', A: 441138.03, B: 352910.43, C: 286739.72, D: 220569.02, E: 154398.31 },
  { id: 11, plots: '51 – 55', A: 419081.13, B: 335264.90, C: 272402.73, D: 209540.57, E: 146678.40 },
  { id: 12, plots: '56 – 60', A: 398127.07, B: 318501.66, C: 258782.60, D: 199063.54, E: 139344.48 },
  { id: 13, plots: '61 – 65', A: 378220.72, B: 302576.58, C: 245843.47, D: 189110.36, E: 132377.25 },
  { id: 14, plots: '66 – 70', A: 359309.68, B: 287447.75, C: 233551.29, D: 179654.84, E: 125758.39 },
  { id: 15, plots: '71 – 75', A: 341344.20, B: 273075.36, C: 221873.73, D: 170672.10, E: 119470.47 },
  { id: 16, plots: '76 – 80', A: 324276.99, B: 259421.59, C: 210780.04, D: 162138.50, E: 113496.95 },
  { id: 17, plots: '81 – 85', A: 308063.14, B: 246450.51, C: 200241.04, D: 154031.57, E: 107822.10 },
  { id: 18, plots: '86 – 90', A: 292659.98, B: 234127.99, C: 190228.99, D: 146329.99, E: 102430.99 },
  { id: 19, plots: '91 – 95', A: 278026.98, B: 222421.59, C: 180717.54, D: 139013.49, E: 97309.44 },
  { id: 20, plots: '96 – 100', A: 264125.64, B: 211300.51, C: 171681.66, D: 132062.82, E: 92443.97 },
];

const EXCESS_RATES = { A: 244981.15, B: 195984.92, C: 159237.75, D: 122490.58, E: 85743.40 };

const LAYOUT_EXCESS_RATES = { A: 260000, B: 210000, C: 170000, D: 130000, E: 92000 };

const TOPO_DATA = [
  { id: 1, size: 'Up to 1,000 m² (5m Grid)', A: 997637, B: 798109.60, C: 648464.05, D: 498818.50, E: 349172.95 },
  { id: 2, size: 'Up to 1 Ha (10m Grid)', A: 2394325, B: 1915460.00, C: 1556311.25, D: 1197162.50, E: 838013.75 },
  { id: 3, size: '5 Ha (10m Grid)', A: 6384867, B: 5107893.60, C: 4150163.55, D: 3192433.50, E: 2234703.45 },
  { id: 4, size: '10 Ha (10m Grid)', A: 11373047, B: 9098437.60, C: 7392480.55, D: 5686523.50, E: 3980566.45 },
  { id: 5, size: '15 Ha (10m Grid)', A: 16361223, B: 13088978.40, C: 10634794.95, D: 8180611.50, E: 5726428.05 },
  { id: 6, size: '20 Ha (10m Grid)', A: 21349403, B: 17079522.40, C: 13877111.95, D: 10674701.50, E: 7472291.05 },
  { id: 7, size: '25 Ha (10m Grid)', A: 26337580, B: 21070064.00, C: 17119427.00, D: 13168790.00, E: 9218153.00 },
  { id: 8, size: '30 Ha (10m Grid)', A: 31325759, B: 25060607.20, C: 20361743.35, D: 15662879.50, E: 10964015.65 },
  { id: 9, size: '35 Ha (10m Grid)', A: 36313936, B: 29051148.80, C: 23604058.40, D: 18156968.00, E: 12709877.60 },
  { id: 10, size: '40 Ha (10m Grid)', A: 41302115, B: 33041692.00, C: 26846374.75, D: 20651057.50, E: 14455740.25 },
  { id: 11, size: '45 Ha (10m Grid)', A: 46290292, B: 37032233.60, C: 30088689.80, D: 23145146.00, E: 16201602.20 },
  { id: 12, size: '50 Ha (10m Grid)', A: 51278471, B: 41022776.80, C: 33331006.15, D: 25639235.50, E: 17947464.85 },
];

const ASBUILT_DATA = [
  { id: 1, plots: '1 – 250', A: 1053503.00, B: 842802.40, C: 684776.95, D: 526751.50, E: 368726.05 },
  { id: 2, plots: '251 – 500', A: 948152.70, B: 758522.16, C: 616299.26, D: 474076.35, E: 331853.45 },
  { id: 3, plots: '501 and above (less 10%)', A: 853337.43, B: 682669.94, C: 554669.33, D: 426668.71, E: 298668.11 },
];

const MAN_MONTH_DATA = [
  { role: 'Chief Resident Surveyor', minYears: '15', rate: 2308549 },
  { role: 'Principal Resident Surveyor', minYears: '12', rate: 2088207 },
  { role: 'Senior Resident Surveyor', minYears: '8', rate: 1593253 },
  { role: 'Resident Surveyor', minYears: '4', rate: 1263041 },
  { role: 'Assist. Resident Surveyor', minYears: 'Under 4', rate: 1044373 },
  { role: 'Senior Technical Officer', minYears: '8', rate: 1566560 },
  { role: 'Technical Officer', minYears: '8', rate: 1044373 },
  { role: 'Assist Technical Officer', minYears: '4', rate: 745980 },
  { role: 'Draughtsman', minYears: '4', rate: 522186 },
];

const CATEGORIES = [
  { key: 'A', label: 'Category A', color: '#0B5D1E', description: 'Premium / High-Density Zones — Maitama I & II, Asokoro, Garki I & II, Wuse I & II, Central Area, Guzape, Wuye, Utako, Katampe Extension, Jabi' },
  { key: 'B', label: 'Category B', color: '#1a7a35', description: 'High Development / Strategic Growth Zones — Gudu, Durumi, Kukwaba National Park, Mabushi, Katampe, Jahi, Kado, Dakibiyu, Kaura, Duboyi, Gaduwa, Dutse, Sector Centres A–D' },
  { key: 'C', label: 'Category C', color: '#2a944d', description: 'Moderate Development / Transitional Zones — Institutions & Research, Karmo, Gwarimpa I & II, Dape, Kafe, Nbora, Galadimawa, Dakwo, Lokogoma, Wumba, Saraji, Kabusa, Okanje, Pyakasa, Wupa, Industrial Areas I & II, Bunkoro, Sector Centres E–J' },
  { key: 'D', label: 'Category D', color: '#3aae65', description: 'Low Development / Peri-Urban Zones — Karsana (East, South, North, West), Idogwari, Idu, Sabon Gida, Tasha, Kagini, Gwagwa, Kaba, Ketti (North, East), Sheretti, Sheretti Cheche, Waru Pozema, Burun (and West), Gidari Bahagwo, Gwari, Bude (and West)' },
  { key: 'E', label: 'Category E', color: '#4ec87d', description: 'Rural / largely undeveloped areas with minimal economic activity — outlying districts across Bwari, Kuje, Kwali, Gwagwalada and Abaji Area Councils (e.g. Giri, Karshi, Jiwa, Lugbe, Kpaduma, Zuba, Dobi, Yangoji, Rubochi, Pegi)' },
];

const FeeSettingsCtx = React.createContext(null);

const fmt = (v) => '₦' + Number(v).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── PERIMETER SURVEY CARD ──────────────────────────────────────────────────

function PerimeterSurveyCard() {
  const ctx = useContext(FeeSettingsCtx);
  const perimeterData = ctx?.perimeter_data ?? PERIMETER_DATA;
  const excessRates = ctx?.excess_rates ?? EXCESS_RATES;
  const categories = ctx?.zone_categories ?? CATEGORIES;
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [manualArea, setManualArea] = useState('');
  const [manualUnit, setManualUnit] = useState('m2');

  const [hectares, setHectares] = useState('');
  const selectedIdx = perimeterData.findIndex((r) => r.id === Number(selectedSize));
  const selectedRow = selectedIdx >= 0 ? perimeterData[selectedIdx] : null;
  const haBounds = selectedRow && selectedRow.per_ha ? getHaBounds(perimeterData, selectedIdx) : null;

  const result = useMemo(() => {
    if (!selectedSize || !selectedCat) return null;
    if (selectedSize === 'other') return perimeterFeeForArea(perimeterData, selectedCat, manualArea, manualUnit, excessRates);
    const idx = perimeterData.findIndex((r) => r.id === Number(selectedSize));
    if (idx < 0) return null;
    const row = perimeterData[idx];
    if (!row.per_ha) {
      const fee = Number(row[selectedCat]);
      return { fee, deposit: fee, size: row.size, cat: selectedCat };
    }
    return computePerHaFee(perimeterData, idx, selectedCat, hectares, excessRates);
  }, [selectedSize, selectedCat, hectares, perimeterData, excessRates, manualArea, manualUnit]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
      {/* Card Header */}
      <div className="bg-[#0B5D1E] px-6 py-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Perimeter Survey</h3>
            <p className="text-white/70 text-sm">Cost per square metres or hectares</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-3 flex-1">
          {/* Plot Size */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Plot Size / Area</label>
            <select
              value={selectedSize}
              onChange={(e) => { setSelectedSize(e.target.value); setHectares(''); }}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition"
            >
              <option value="">-- Select plot size --</option>
              {perimeterData.map((row) => (
                <option key={row.id} value={row.id}>{row.size}</option>
              ))}
              <option value="other">Other — enter area manually</option>
            </select>
          </div>
          {selectedSize === 'other' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Enter Area</label>
              <div className="flex gap-2">
                <input type="number" min="1" step="any" placeholder="e.g. 2500" value={manualArea} onChange={(e) => setManualArea(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition" />
                <select value={manualUnit} onChange={(e) => setManualUnit(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm outline-none">
                  <option value="m2">m²</option>
                  <option value="ha">Ha</option>
                </select>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">The matching band is applied automatically; areas above 50 Ha attract the excess rate per hectare.</p>
            </div>
          )}
          {haBounds && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Area in Hectares</label>
              <input type="number" step="0.1" min={haBounds.minHa} max={haBounds.isLast ? undefined : haBounds.haMax}
                value={hectares} onChange={(e) => setHectares(e.target.value)} placeholder={`e.g. ${haBounds.haMax}`}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none transition" />
              <p className="text-[11px] text-gray-500 mt-1">
                {haBounds.haPrev > 0 ? `Band ${selectedRow.size}: enter ${haBounds.minHa} – ${haBounds.haMax} Ha` : 'Exactly 1 Ha'}
                {haBounds.isLast ? ' (above 50 Ha the excess rate applies)' : ''}
                {selectedCat ? ` · Rate ${fmt(selectedRow[selectedCat])}/Ha` : ''}
              </p>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Zone Category</label>
            <div className="grid grid-cols-5 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCat(cat.key)}
                  className={`py-2 rounded-lg text-xs font-bold transition-all ${
                    selectedCat === cat.key
                      ? 'text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={selectedCat === cat.key ? { backgroundColor: cat.color } : {}}
                >
                  {cat.key}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Result */}
        {result ? (
          <div className="mt-4 bg-gradient-to-br from-[#0B5D1E]/5 to-[#0B5D1E]/10 rounded-lg p-4 border border-[#0B5D1E]/15">
            <div className="text-xs font-semibold text-[#0B5D1E] uppercase tracking-wider mb-1">
              Minimum Survey Fee
            </div>
            <div className="text-2xl font-extrabold text-[#0B5D1E] mb-3">
              {fmt(result.fee)}
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#0B5D1E]/10">
              <span className="text-sm text-gray-600">Mandatory Deposit (100%)</span>
              <span className="text-base font-bold text-red-600">{fmt(result.deposit)}</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{result.size} — Zone {result.cat}</span>
            </div>
            {result.perHa && (
              <div className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                {result.haPrev > 0 ? `Cumulative to ${result.haPrev} Ha: ${fmt(result.base)} + ` : ''}
                {`${result.within} Ha × ${fmt(result.rate)}/Ha`}
                {result.excessHa > 0 ? ` + ${result.excessHa} Ha × ${fmt(result.excessRate)}/Ha (excess above ${result.haMax} Ha)` : ''}
              </div>
            )}
            {result.note && (
              <div className="mt-2 text-[11px] text-gray-500 leading-relaxed">{result.note}</div>
            )}
          </div>
        ) : (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 text-center">
            <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-400">Select plot size and zone category to calculate fee</p>
          </div>
        )}
      </div>

      {/* Excess note */}
      <div className="px-6 pb-5">
        <details className="group">
          <summary className="text-xs font-semibold text-[#0B5D1E] cursor-pointer select-none flex items-center gap-1 hover:underline">
            <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Areas exceeding 50 Ha
          </summary>
          <div className="mt-2 bg-amber-50 rounded-lg p-3 border border-amber-200 text-xs text-amber-800 space-y-1">
            {Object.entries(excessRates).map(([zone, rate]) => (
              <div key={zone} className="flex justify-between">
                <span>Zone "{zone}"</span>
                <span className="font-semibold">{fmt(rate)}/Ha (additional)</span>
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

// ─── LAYOUT SURVEY CARD ─────────────────────────────────────────────────────

function LayoutSurveyCard() {
  const ctx = useContext(FeeSettingsCtx);
  const layoutData = ctx?.layout_data ?? LAYOUT_DATA;
  const layoutExcessRates = ctx?.layout_excess_rates ?? LAYOUT_EXCESS_RATES;
  const categories = ctx?.zone_categories ?? CATEGORIES;
  const [selectedPlots, setSelectedPlots] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [numPlots, setNumPlots] = useState('');

  const result = useMemo(() => {
    if (!selectedPlots || !selectedCat) return null;
    if (selectedPlots === 'other') return layoutFeeForPlots(layoutData, selectedCat, numPlots, layoutExcessRates);
    const row = layoutData.find((r) => r.id === Number(selectedPlots));
    if (!row) return null;
    const costPerPlot = row[selectedCat];
    const qty = numPlots ? Math.max(1, parseInt(numPlots, 10) || 1) : 1;
    const totalCost = costPerPlot * qty;
    const deposit = totalCost;
    return { costPerPlot, totalCost, deposit, plots: row.plots, cat: selectedCat, qty };
  }, [selectedPlots, selectedCat, numPlots, layoutData, layoutExcessRates]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
      {/* Card Header */}
      <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #1a3a5c 0%, #0d2847 100%)' }}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zm10 0a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Layout Survey</h3>
            <p className="text-white/70 text-sm">Cost per plot by number of plots</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-3 flex-1">
          {/* Number of plots range */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Number of Plots (Range)</label>
            <select
              value={selectedPlots}
              onChange={(e) => setSelectedPlots(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#1a3a5c]/30 focus:border-[#1a3a5c] outline-none transition"
            >
              <option value="">-- Select number of plots --</option>
              {layoutData.map((row) => (
                <option key={row.id} value={row.id}>{row.plots} plots</option>
              ))}
              <option value="other">Other — enter exact number of plots</option>
            </select>
          </div>
          {selectedPlots === 'other' && (
            <p className="text-[11px] text-gray-500 -mt-1">Enter the exact number of plots below; the matching band is applied automatically and plots above 100 attract the excess rate.</p>
          )}

          {/* Exact number of plots */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Exact Number of Plots <span className="text-gray-400 font-normal">(optional — for total cost)</span>
            </label>
            <input
              type="number"
              min="1"
              max="999"
              placeholder="e.g. 12"
              value={numPlots}
              onChange={(e) => setNumPlots(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#1a3a5c]/30 focus:border-[#1a3a5c] outline-none transition"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Zone Category</label>
            <div className="grid grid-cols-5 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCat(cat.key)}
                  className={`py-2 rounded-lg text-xs font-bold transition-all ${
                    selectedCat === cat.key
                      ? 'text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={selectedCat === cat.key ? { backgroundColor: '#1a3a5c' } : {}}
                >
                  {cat.key}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Result */}
        {result ? (
          <div className="mt-4 bg-gradient-to-br from-[#1a3a5c]/5 to-[#1a3a5c]/10 rounded-lg p-4 border border-[#1a3a5c]/15">
            <div className="text-xs font-semibold text-[#1a3a5c] uppercase tracking-wider mb-1">
              Cost Per Plot
            </div>
            <div className="text-2xl font-extrabold text-[#1a3a5c] mb-3">
              {fmt(result.costPerPlot)}
            </div>
            <div className="space-y-2">
              {result.qty > 1 && (
                <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#1a3a5c]/10">
                  <span className="text-sm text-gray-600">Total ({result.qty} plots)</span>
                  <span className="text-base font-bold text-[#1a3a5c]">{fmt(result.totalCost)}</span>
                </div>
              )}
              <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#1a3a5c]/10">
                <span className="text-sm text-gray-600">Mandatory Deposit (100%)</span>
                <span className="text-base font-bold text-red-600">{fmt(result.deposit)}</span>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{result.plots} plots — Zone {result.cat}</span>
            </div>
            {result.note && (
              <div className="mt-2 text-[11px] text-gray-500 leading-relaxed">{result.note}</div>
            )}
          </div>
        ) : (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 text-center">
            <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-400">Select number of plots and zone category to calculate fee</p>
          </div>
        )}
      </div>

      <div className="px-6 pb-5">
        <details className="group">
          <summary className="text-xs font-semibold text-[#1a3a5c] cursor-pointer select-none flex items-center gap-1 hover:underline">
            <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Jobs exceeding 100 plots
          </summary>
          <div className="mt-2 bg-amber-50 rounded-lg p-3 border border-amber-200 text-xs text-amber-800 space-y-1">
            {Object.entries(layoutExcessRates).map(([zone, rate]) => (
              <div key={zone} className="flex justify-between">
                <span>Zone "{zone}"</span>
                <span className="font-semibold">{fmt(rate)}/plot (additional)</span>
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

// ─── TOPOGRAPHICAL SURVEY CARD ───────────────────────────────────────────────

function TopographicalSurveyCard() {
  const ctx = useContext(FeeSettingsCtx);
  const topoData = ctx?.topo_data ?? TOPO_DATA;
  const categories = ctx?.zone_categories ?? CATEGORIES;
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [manualArea, setManualArea] = useState('');
  const [manualUnit, setManualUnit] = useState('m2');

  const result = useMemo(() => {
    if (!selectedSize || !selectedCat) return null;
    if (selectedSize === 'other') return topoFeeForArea(topoData, selectedCat, manualArea, manualUnit);
    const row = topoData.find((r) => r.id === Number(selectedSize));
    if (!row) return null;
    const fee = row[selectedCat];
    const deposit = fee;
    return { fee, deposit, size: row.size, cat: selectedCat };
  }, [selectedSize, selectedCat, manualArea, manualUnit, topoData]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #7B2D26 0%, #5a1d18 100%)' }}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Topographical Survey</h3>
            <p className="text-white/70 text-sm">Cost by area size and grid spacing</p>
          </div>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-3 flex-1">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Area Size</label>
            <select
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#7B2D26]/30 focus:border-[#7B2D26] outline-none transition"
            >
              <option value="">-- Select area size --</option>
              {topoData.map((row) => (
                <option key={row.id} value={row.id}>{row.size}</option>
              ))}
              <option value="other">Other — enter area manually</option>
            </select>
          </div>
          {selectedSize === 'other' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Enter Area</label>
              <div className="flex gap-2">
                <input type="number" min="1" step="any" placeholder="e.g. 2500" value={manualArea} onChange={(e) => setManualArea(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#7B2D26]/30 focus:border-[#7B2D26] outline-none transition" />
                <select value={manualUnit} onChange={(e) => setManualUnit(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm outline-none">
                  <option value="m2">m²</option>
                  <option value="ha">Ha</option>
                </select>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Uses the alternative procedure: up to 1,000 m², then 1 Ha, then every further hectare at the 1,000 m² rate.</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Zone Category</label>
            <div className="grid grid-cols-5 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCat(cat.key)}
                  className={`py-2 rounded-lg text-xs font-bold transition-all ${
                    selectedCat === cat.key
                      ? 'text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={selectedCat === cat.key ? { backgroundColor: '#7B2D26' } : {}}
                >
                  {cat.key}
                </button>
              ))}
            </div>
          </div>
        </div>

        {result ? (
          <div className="mt-4 bg-gradient-to-br from-[#7B2D26]/5 to-[#7B2D26]/10 rounded-lg p-4 border border-[#7B2D26]/15">
            <div className="text-xs font-semibold text-[#7B2D26] uppercase tracking-wider mb-1">Survey Cost</div>
            <div className="text-2xl font-extrabold text-[#7B2D26] mb-3">{fmt(result.fee)}</div>
            <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#7B2D26]/10">
              <span className="text-sm text-gray-600">Mandatory Deposit (100%)</span>
              <span className="text-base font-bold text-red-600">{fmt(result.deposit)}</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{result.size} — Zone {result.cat}</span>
            </div>
            {result.note && (
              <div className="mt-2 text-[11px] text-gray-500 leading-relaxed">{result.note}</div>
            )}
          </div>
        ) : (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 text-center">
            <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-400">Select area size and zone category to calculate fee</p>
          </div>
        )}
      </div>

      <div className="px-6 pb-5">
        <details className="group">
          <summary className="text-xs font-semibold text-[#7B2D26] cursor-pointer select-none flex items-center gap-1 hover:underline">
            <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Alternative calculation
          </summary>
          <div className="mt-2 bg-amber-50 rounded-lg p-3 border border-amber-200 text-xs text-amber-800 space-y-1">
            <div className="flex justify-between"><span>Up to 1,000 sqm</span><span className="font-semibold">{fmt(997637)}</span></div>
            <div className="flex justify-between"><span>1 Ha</span><span className="font-semibold">{fmt(2394325)}</span></div>
            <div className="flex justify-between"><span>Every next 1 Ha and above</span><span className="font-semibold">{fmt(997637)} (additional)</span></div>
          </div>
        </details>
      </div>
    </div>
  );
}

// ─── AS-BUILT / DETAIL SURVEY CARD ──────────────────────────────────────────

function AsBuiltSurveyCard() {
  const ctx = useContext(FeeSettingsCtx);
  const asbuiltData = ctx?.asbuilt_data ?? ASBUILT_DATA;
  const categories = ctx?.zone_categories ?? CATEGORIES;
  const [selectedPlots, setSelectedPlots] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [numPlots, setNumPlots] = useState('');

  const result = useMemo(() => {
    if (!selectedPlots || !selectedCat) return null;
    if (selectedPlots === 'other') return asbuiltFeeForPlots(asbuiltData, selectedCat, numPlots);
    const row = asbuiltData.find((r) => r.id === Number(selectedPlots));
    if (!row) return null;
    const costPerPlot = row[selectedCat];
    const qty = numPlots ? Math.max(1, parseInt(numPlots, 10) || 1) : 1;
    const totalCost = costPerPlot * qty;
    const deposit = totalCost;
    return { costPerPlot, totalCost, deposit, plots: row.plots, cat: selectedCat, qty };
  }, [selectedPlots, selectedCat, numPlots, asbuiltData]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #6B4C9A 0%, #4a3470 100%)' }}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold text-white">As-Built / Detail Survey</h3>
            <p className="text-white/70 text-sm">Unit cost per plot</p>
          </div>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-3 flex-1">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Number of Plots (Range)</label>
            <select
              value={selectedPlots}
              onChange={(e) => setSelectedPlots(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#6B4C9A]/30 focus:border-[#6B4C9A] outline-none transition"
            >
              <option value="">-- Select number of plots --</option>
              {asbuiltData.map((row) => (
                <option key={row.id} value={row.id}>{row.plots} plots</option>
              ))}
              <option value="other">Other — enter exact number of plots</option>
            </select>
          </div>
          {selectedPlots === 'other' && (
            <p className="text-[11px] text-gray-500 -mt-1">Enter the exact number of plots below; the matching band is applied automatically.</p>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Exact Number of Plots <span className="text-gray-400 font-normal">(optional — for total cost)</span>
            </label>
            <input
              type="number"
              min="1"
              max="999"
              placeholder="e.g. 25"
              value={numPlots}
              onChange={(e) => setNumPlots(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#6B4C9A]/30 focus:border-[#6B4C9A] outline-none transition"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Zone Category</label>
            <div className="grid grid-cols-5 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCat(cat.key)}
                  className={`py-2 rounded-lg text-xs font-bold transition-all ${
                    selectedCat === cat.key
                      ? 'text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={selectedCat === cat.key ? { backgroundColor: '#6B4C9A' } : {}}
                >
                  {cat.key}
                </button>
              ))}
            </div>
          </div>
        </div>

        {result ? (
          <div className="mt-4 bg-gradient-to-br from-[#6B4C9A]/5 to-[#6B4C9A]/10 rounded-lg p-4 border border-[#6B4C9A]/15">
            <div className="text-xs font-semibold text-[#6B4C9A] uppercase tracking-wider mb-1">Unit Cost Per Plot</div>
            <div className="text-2xl font-extrabold text-[#6B4C9A] mb-3">{fmt(result.costPerPlot)}</div>
            <div className="space-y-2">
              {result.qty > 1 && (
                <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#6B4C9A]/10">
                  <span className="text-sm text-gray-600">Total ({result.qty} plots)</span>
                  <span className="text-base font-bold text-[#6B4C9A]">{fmt(result.totalCost)}</span>
                </div>
              )}
              <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#6B4C9A]/10">
                <span className="text-sm text-gray-600">Mandatory Deposit (100%)</span>
                <span className="text-base font-bold text-red-600">{fmt(result.deposit)}</span>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{result.plots} plots — Zone {result.cat}</span>
            </div>
            {result.note && (
              <div className="mt-2 text-[11px] text-gray-500 leading-relaxed">{result.note}</div>
            )}
          </div>
        ) : (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 text-center">
            <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-400">Select number of plots and zone category to calculate fee</p>
          </div>
        )}
      </div>

      <div className="px-6 pb-5">
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 text-xs text-amber-800">
          <span className="font-semibold">501 units and above:</span> unit cost is 10% less than the 251 – 500 rate (already reflected in the table).
        </div>
      </div>
    </div>
  );
}

// ─── ENGINEERING SURVEYS CARD ───────────────────────────────────────────────

function EngineeringSurveysCard() {
  const ctx = useContext(FeeSettingsCtx);
  const engSettings = ctx?.engineering_settings ?? {
    setting_out_base_fee: 300000, extra_corner_rate: 30000,
    route_base_fee: 957730, route_extra_500m_rate: 700000,
  };
  const [surveyType, setSurveyType] = useState('');
  const [corners, setCorners] = useState('');
  const [routeLength, setRouteLength] = useState('');
  const [otherDesc, setOtherDesc] = useState('');
  const [otherFee, setOtherFee] = useState('');

  const result = useMemo(() => {
    if (!surveyType) return null;
    if (surveyType === 'other') {
      const fee = parseFloat(otherFee);
      if (!fee || fee <= 0 || Number.isNaN(fee)) return null;
      return { fee, deposit: fee, desc: `${otherDesc.trim() || 'Other survey'} — agreed fee` };
    }

    if (surveyType === 'setting-out') {
      const c = parseInt(corners, 10) || 4;
      const clampedCorners = Math.max(4, c);
      const extraCorners = clampedCorners > 6 ? (clampedCorners - 6) * engSettings.extra_corner_rate : 0;
      const fee = engSettings.setting_out_base_fee + extraCorners;
      const deposit = fee;
      return { fee, deposit, desc: `${clampedCorners} corners${clampedCorners > 6 ? ` (${clampedCorners - 6} extra)` : ''}` };
    }

    if (surveyType === 'route') {
      const m = parseInt(routeLength, 10) || 500;
      const clampedLen = Math.max(1, m);
      const extra500s = clampedLen > 500 ? Math.ceil((clampedLen - 500) / 500) : 0;
      const fee = engSettings.route_base_fee + extra500s * engSettings.route_extra_500m_rate;
      const deposit = fee;
      return { fee, deposit, desc: `${clampedLen.toLocaleString()}m route${extra500s > 0 ? ` (+${extra500s} × 500m)` : ''}` };
    }

    return null;
  }, [surveyType, corners, routeLength, engSettings, otherDesc, otherFee]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #B8860B 0%, #8B6914 100%)' }}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Engineering Surveys</h3>
            <p className="text-white/70 text-sm">Setting Out & Route Survey</p>
          </div>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-3 flex-1">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Survey Type</label>
            <select
              value={surveyType}
              onChange={(e) => setSurveyType(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#B8860B]/30 focus:border-[#B8860B] outline-none transition"
            >
              <option value="">-- Select survey type --</option>
              <option value="setting-out">Setting Out of Buildings</option>
              <option value="route">Route Survey</option>
              <option value="other">Other — enter agreed fee</option>
            </select>
          </div>
          {surveyType === 'other' && (
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Survey Description</label>
                <input type="text" placeholder="e.g. Pipeline detection survey" value={otherDesc} onChange={(e) => setOtherDesc(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#B8860B]/30 focus:border-[#B8860B] outline-none transition" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Agreed / Negotiated Fee (₦)</label>
                <input type="number" min="0" step="any" placeholder="e.g. 1500000" value={otherFee} onChange={(e) => setOtherFee(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#B8860B]/30 focus:border-[#B8860B] outline-none transition" />
              </div>
              <p className="text-[11px] text-gray-500">Special surveys are negotiated on man-month rates (see the Man-Month card). Enter the agreed fee to compute the 100% mandatory deposit.</p>
            </div>
          )}

          {surveyType === 'setting-out' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Number of Corners <span className="text-gray-400 font-normal">(min 4, base covers up to 6)</span>
              </label>
              <input
                type="number"
                min="4"
                max="100"
                placeholder="e.g. 8"
                value={corners}
                onChange={(e) => setCorners(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#B8860B]/30 focus:border-[#B8860B] outline-none transition"
              />
              <p className="mt-2 text-xs text-gray-500">
                Base fee: <span className="font-semibold">{fmt(engSettings.setting_out_base_fee)}</span> for 4–6 corners. Extra corners: <span className="font-semibold">{fmt(engSettings.extra_corner_rate)}</span> each.
              </p>
            </div>
          )}

          {surveyType === 'route' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Route Length in Meters
              </label>
              <input
                type="number"
                min="1"
                placeholder="e.g. 1500"
                value={routeLength}
                onChange={(e) => setRouteLength(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm focus:ring-2 focus:ring-[#B8860B]/30 focus:border-[#B8860B] outline-none transition"
              />
              <p className="mt-2 text-xs text-gray-500">
                Base fee: <span className="font-semibold">{fmt(engSettings.route_base_fee)}</span> for up to 500m. Each additional 500m: <span className="font-semibold">{fmt(engSettings.route_extra_500m_rate)}</span>.
              </p>
            </div>
          )}
        </div>

        {result ? (
          <div className="mt-4 bg-gradient-to-br from-[#B8860B]/5 to-[#B8860B]/10 rounded-lg p-4 border border-[#B8860B]/15">
            <div className="text-xs font-semibold text-[#B8860B] uppercase tracking-wider mb-1">Survey Fee</div>
            <div className="text-2xl font-extrabold text-[#B8860B] mb-3">{fmt(result.fee)}</div>
            <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-[#B8860B]/10">
              <span className="text-sm text-gray-600">Mandatory Deposit (100%)</span>
              <span className="text-base font-bold text-red-600">{fmt(result.deposit)}</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{result.desc}</span>
            </div>
          </div>
        ) : (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 text-center">
            <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-400">Select a survey type and enter details to calculate fee</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAN-MONTH RATES CARD ───────────────────────────────────────────────────

function ManMonthRatesCard() {
  const ctx = useContext(FeeSettingsCtx);
  const manMonthData = ctx?.man_month_data ?? MAN_MONTH_DATA;
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
      <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #2F4F4F 0%, #1a3333 100%)' }}>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Man-Month Rates</h3>
            <p className="text-white/70 text-sm">Time charges for survey projects (2024 CPI)</p>
          </div>
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="space-y-1 flex-1">
          {manMonthData.map((item, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between px-4 py-3 rounded-lg ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{item.role}</div>
                <div className="text-xs text-gray-500">{item.minYears} {item.minYears === 'Under 4' ? '' : 'yrs'} min. experience</div>
              </div>
              <div className="text-sm font-bold text-[#2F4F4F] whitespace-nowrap ml-3">{fmt(item.rate)}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 bg-amber-50 rounded-xl p-4 border border-amber-200">
          <div className="flex gap-2">
            <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-xs text-amber-800 space-y-1">
              <p className="font-semibold">Working Duration</p>
              <p>Year = 10 months (200 days)</p>
              <p>Month = 20 days, 8 hours/day</p>
              <p className="mt-2 text-amber-700 italic">To obtain 2025+ figures, apply the Central Bank inflation index for the preceding year.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────

export default function ScaleOfFees() {
  const [feeSettings, setFeeSettings] = useState(null);

  useEffect(() => {
    api.get('/scale-of-fees/settings/')
      .then((res) => setFeeSettings(res.data))
      .catch(() => {});
  }, []);

  return (
    <FeeSettingsCtx.Provider value={feeSettings}>
    <>
      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-[#0B5D1E]" style={{ backgroundImage: 'linear-gradient(135deg, #0B5D1E 0%, #094A18 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        </div>
        <div className="relative" style={{ paddingTop: 80, paddingBottom: 80, zIndex: 1 }}>
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Scale of Fees for Survey Services
            </h1>
            <p className="text-white/80 text-lg max-w-3xl mx-auto">
              NIS FCT Branch Professional Scale of Fees 2026 — APPSN FCT Branch
            </p>
          </div>
        </div>
      </section>

      {/* Intro + Mandatory Notice */}
      <section className="py-10 md:py-14 bg-[#f0ede8]">
        <div className="max-w-5xl mx-auto px-4 space-y-6">
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm">
            <p className="text-gray-700 leading-relaxed text-[15px]">
              This scale of fees is developed to allow Surveyors charge as Consultants and also charge for the <strong>"Direct Costs"</strong> for the execution of survey projects. The Consultancy Fees is based on values of properties or cost of projects and reimbursable; as contained in the 1996 Scale of Fees for Consultants.
            </p>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-5 md:p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-red-800 mb-1">Mandatory Notice</h3>
                <p className="text-red-700 text-sm leading-relaxed">
                  A <strong>Mandatory Deposit Scheme (MDS) fee of 100%</strong> of the cost of Survey shall be enforced for all Survey jobs within FCT. Every Surveyor applying for pillar number(s) or Instruction to Survey (I to S) must adhere strictly to this mandatory payment before approval is granted accordingly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zone Categories Explanation */}
      <section className="py-12 md:py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Zone Categories
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-4">
            {(feeSettings?.zone_categories ?? CATEGORIES).map((cat) => (
              <div key={cat.key} className="rounded-xl p-4 text-center border border-gray-100 shadow-sm" style={{ backgroundColor: cat.color + '08', borderColor: cat.color + '20' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-lg" style={{ backgroundColor: cat.color }}>
                  {cat.key}
                </div>
                <h3 className="text-sm font-bold text-gray-800">{cat.label}</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  {cat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fee Calculator Section - Two Cards */}
      <section className="py-12 md:py-16 bg-[#f0ede8]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Fee Calculator
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              Select your survey type, plot size, and zone category to instantly calculate the minimum survey fee
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <PerimeterSurveyCard />
            <LayoutSurveyCard />
            <TopographicalSurveyCard />
            <AsBuiltSurveyCard />
            <EngineeringSurveysCard />
            <ManMonthRatesCard />
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 md:py-14 bg-[#f0ede8]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="bg-[#0B5D1E] rounded-2xl p-8 md:p-12 shadow-lg">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Need a Detailed Quotation?
            </h3>
            <p className="text-white/80 mb-6 max-w-md mx-auto text-sm">
              Contact the APPSN FCT Branch secretariat for specific fee quotations, consultancy fee assessment, or survey service enquiries.
            </p>
            <a href="/find-surveyor" className="inline-block bg-white text-[#0B5D1E] font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition">
              Find a Surveyor
            </a>
          </div>
        </div>
      </section>
    </>
    </FeeSettingsCtx.Provider>
  );
}
